/**
 * @file useResizableColumns.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-21
 * @modified 2026-05-21
 * @description Shared drag-to-resize columns hook.
 *
 * Usage:
 *   const { tableRef, ResizeHandle, ColGroup } =
 *     useResizableColumns('searchInventory', 11);
 *
 *   <table ref={tableRef} className="data-table resizable-table">
 *     <ColGroup />
 *     <thead>
 *       <tr>
 *         <th>...<ResizeHandle colIndex={0} /></th>
 *         ...
 *       </tr>
 *     </thead>
 *     ...
 *   </table>
 *
 * Plus add `import './ResizableColumns.css'` in the component or its CSS so
 * the .resizable-table base rules + handle styling are in scope. Then add
 * any per-column min-widths via your component CSS using
 * `.<scope> .data-table tbody td:nth-child(N)` etc.
 *
 * Widths persist via usePersistedState under `${pageKey}.columnWidths` so
 * navigating away and back preserves the user's layout.
 */

import { useRef, useState, useCallback } from 'react';
import usePersistedState from './usePersistedState';

export default function useResizableColumns(pageKey, columnCount) {
  const [columnWidths, setColumnWidths] = usePersistedState(`${pageKey}.columnWidths`, {});
  const [resizingIdx, setResizingIdx] = useState(null);
  const tableRef = useRef(null);
  const resizeStateRef = useRef(null);

  const startResize = useCallback((colIndex) => (e) => {
    // Don't trigger the th's click (which would sort), don't bubble.
    e.preventDefault();
    e.stopPropagation();

    const ths = tableRef.current?.querySelectorAll('thead > tr > th');
    const startWidth = ths?.[colIndex]?.getBoundingClientRect().width || 100;
    resizeStateRef.current = { colIndex, startX: e.clientX, startWidth };
    setResizingIdx(colIndex);
    document.body.classList.add('col-resizing');

    const onMove = (ev) => {
      const s = resizeStateRef.current;
      if (!s) return;
      // Floor at 30px — anything narrower is unusable. CSS min-widths
      // per column can raise this on a per-table basis.
      const newWidth = Math.max(30, Math.round(s.startWidth + (ev.clientX - s.startX)));
      setColumnWidths(prev => ({ ...prev, [s.colIndex]: newWidth }));
    };

    const onUp = () => {
      resizeStateRef.current = null;
      setResizingIdx(null);
      document.body.classList.remove('col-resizing');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [setColumnWidths]);

  // Render a resize handle at the right edge of a <th>. Handle swallows
  // mousedown + click so the th's click handler (sort) doesn't also fire.
  const ResizeHandle = ({ colIndex }) => (
    <span
      className={`col-resize-handle${resizingIdx === colIndex ? ' resizing' : ''}`}
      onMouseDown={startResize(colIndex)}
      onClick={(e) => e.stopPropagation()}
      title="Drag to resize column"
    />
  );

  // <colgroup> driven by columnWidths. Unset widths fall through to browser
  // defaults until the user drags them. table-layout: fixed (from the
  // shared .resizable-table CSS) makes these widths authoritative.
  const ColGroup = () => (
    <colgroup>
      {Array.from({ length: columnCount }, (_, i) => (
        <col
          key={i}
          style={columnWidths[i] ? { width: `${columnWidths[i]}px` } : undefined}
        />
      ))}
    </colgroup>
  );

  // Drop every persisted width so columns fall back to the per-table
  // default widths defined in CSS (col:nth-child rules).
  const resetWidths = useCallback(() => {
    setColumnWidths({});
  }, [setColumnWidths]);

  // Pre-styled toolbar button — pass `className` to override placement.
  // Stays disabled when no widths have been changed, so the user knows the
  // table is already at defaults.
  const ResetButton = ({ className = '', label = 'Reset columns', title = 'Reset column widths to default' }) => {
    const hasOverrides = Object.keys(columnWidths || {}).length > 0;
    return (
      <button
        type="button"
        className={`btn-secondary btn-sm ${className}`}
        onClick={resetWidths}
        disabled={!hasOverrides}
        title={title}
      >
        <span className="material-symbols-rounded">restart_alt</span>
        {label}
      </button>
    );
  };

  return {
    tableRef,
    ResizeHandle,
    ColGroup,
    ResetButton,
    columnWidths,
    setColumnWidths,
    resetWidths,
    resizingIdx,
  };
}
